import { useEffect, useState } from "react"
import { DetailPage, Row } from "../../components/crud/DetailPage"
import { FkLink, ListPage } from "../../components/crud/ListPage"
import { AsyncSelect, Checkbox, DateInput, EmailInput, FileInput, ImageInput, NumberInput, PhoneInput, Select, TextareaInput, TextInput } from "../../components/forms/Inputs"
import { useTranslations } from "../../layers/Localization"
import { useTitleOptions } from "./Contacts"
import { TempsList } from "./Temps"
import { useNavigate, useParams } from "react-router-dom"
import { useApi } from "../../hooks/useApi"
import { HistoriquesList } from "./Historique"
import { useOptions } from "../../hooks/useOptions"
import { postalCodeToCommune } from "../../hooks/helpers"
import { useObjectState } from "../../hooks/useObjectState"
import { Popup } from "../../components/layout/Popup"
import { Form } from "../../components/forms/Form"
import { Button } from "../../components/layout/Button"
import { FaBan, FaSave } from "react-icons/fa"
import { useToday } from "../../hooks/useToday"

export const useFonctionOptions = () => {
    const t = useTranslations()
    return [
        { value: 'ADMINISTRATEUR_DELEGUE', label: t("Administrateur Délégué") },
        { value: 'RESPONSABLE_COMMERCIAL', label: t("Responsable Commercial") },
        { value: 'INSPECTEUR', label: t("Inspecteur") },
        { value: 'CHEF_CHANTIER', label: t("Chef de chantier") },
        { value: 'CHEF_EQUIPE', label: t("Chef d'équipe") },
        { value: 'PREPOSE', label: t("Préposé") },
    ]
}

const useActions = () => {
    const t = useTranslations()
    const today = useToday()

    return {
        [t('Aperçu prestations')]: x => window.open(`${process.env.REACT_APP_API_URL}/personnel/${x.id}/prestations?date=${encodeURIComponent(today.toISOString())}`, '_blank').focus()
    }
}

function PersonnelFields({ item, set }) {
    const [temps, setTemps] = useState([])
    const t = useTranslations()
    const fournisseurOptions = useOptions('fournisseurs', 'nom')
    const titleOptions = useTitleOptions()
    const fonctionOptions = useFonctionOptions()
    const { id } = useParams()
    const api = useApi()

    useEffect(() => {
        if (id === 'new') api.get(`/personnel?limit=1&orderBy:desc=num`).then(personnels => set('num')((personnels[0]?.num || 0) + 1))
        else api.get(`/temps?personnelId=${encodeURIComponent(id)}&dateFin=&chantier:join=`).then(setTemps)
    }, [id])

    // const changeGeodynamics = value => {
    //     set('geodynamics')(value)
    //     set('poiGroup')(value ? `Chantiers de ${item.prenom}` : null)
    // }
    const onCodePostalChange = value => {
        set('cp')(value)
        set('commune')(postalCodeToCommune(value))
    }

    const openTemps = temps.filter(x => !x.dateFin)
    const invalidLastDate = item.dateFin && openTemps.length && t("Impossible de fermer le chantier car il reste des temps non cloturés: {temps}", { temps: openTemps.map(x => x.chantier?.nom).join(', ') })

    return <>
        <Row>
            <NumberInput label={t("Numéro")} value={item.num} onChange={set('num')} required max="999999" />
            <Select label={t("Titre")} value={item.titre} onChange={set('titre')} options={titleOptions} />
            <TextInput className="grow" label={t("Nom")} value={item.nom} onChange={set('nom')} />
            <TextInput className="grow" label={t("Prénom")} value={item.prenom} onChange={set('prenom')} />
        </Row>
        <Row>
            <Select label={t("Fonction")} value={item.fonction} onChange={set('fonction')} options={fonctionOptions} required />
            <AsyncSelect className="grow" label={t("Sous-traitant")} value={item.fournisseurId} onChange={set('fournisseurId')} options={fournisseurOptions} />
        </Row>
        <Checkbox label={t("Signataire")} value={item.signataire} onChange={set('signataire')} />
        <hr />
        <Row start>
            <ImageInput label={t("Photo")} value={item.photo} onChange={set('photo')} />
            <div className="grow flex flex-col gap-5">
                <Row>
                    <DateInput label={t("Date de naissance")} value={item.dateDeNaissance} onChange={set('dateDeNaissance')} />
                    <TextInput className="grow" label={t("Nationalité")} value={item.nationalite} onChange={set('nationalite')} />
                </Row>
                <Row>
                    <TextInput className="grow" label={t("Numéro national")} value={item.numNational} onChange={set('numNational')} />
                    <DateInput label={t("Date d'expiration du document d'identité")} value={item.expirationDocumentIdentite} onChange={set('expirationDocumentIdentite')} />
                </Row>
                <Checkbox label={t("Limosa")} value={item.limosa} onChange={set('limosa')} />
            </div>
        </Row>
        <hr />
        <Row>
            <TextInput className="grow" label={t("Adresse")} value={item.adresse} onChange={set('adresse')} />
            <TextInput label={t("Code postal")} value={item.cp} onChange={onCodePostalChange} />
            <TextInput label={t("Commune")} value={item.commune} onChange={set('commune')} />
        </Row>
        <Row>
            <PhoneInput label={t("Téléphone")} value={item.telephone} onChange={set('telephone')} />
            <PhoneInput label={t("Mobile")} value={item.mobile} onChange={set('mobile')} />
            <EmailInput className="grow" label={t("Email")} value={item.email} onChange={set('email')} />
        </Row>
        <hr />
        <Row>
            <DateInput label={t("Date de début")} value={item.dateDebut} onChange={set('dateDebut')} />
            <DateInput label={t("Date de fin")} value={item.dateFin} onChange={set('dateFin')} invalid={invalidLastDate} />
            <TextInput className="grow" label={t("Raison de départ")} value={item.raisonDeDepart} onChange={set('raisonDeDepart')} />
        </Row>
        <hr />
        {/* <Checkbox label={t("Geodynamics")} value={item.geodynamics} onChange={changeGeodynamics} />
        <Row>
            <TextInput label={t("POI Group")} value={item.poiGroup} onChange={set('poiGroup')} />
        </Row>
        <hr /> */}
        <TextareaInput className="[&_textarea]:text-red-600" label={t("Mémo interne")} value={item.memoInterne} onChange={set('memoInterne')} />
    </>
}

export function PersonnelList(props) {
    const [copiedPersonnel, setCopiedPersonnel] = useState()
    const [reload, setReload] = useState()
    const t = useTranslations()
    const today = useToday()
    const navigate = useNavigate()

    const actions = {
        ...useActions(),
        [t("Copier prestations d'un autre préposé")]: (item, reload) => {
            setCopiedPersonnel(item)
            setReload(reload)
        }
    }

    return <>
        <ListPage model="personnel" title={t("Personnel")} columns={{
            [t('Numéro')]: 'num',
            [t('Prénom')]: 'prenom',
            [t("Sous-traitant")]: { sort: 'fournisseur', value: item => <FkLink>{item.fournisseur?.nom}</FkLink> },
            [t('Email')]: 'email',
            [t('Mobile')]: 'mobile',
        }} join={{
            fournisseur: 'nom'
        }} mainActions={{
            [t('Aperçu prestations fréquentielles')]: () => window.open(`${process.env.REACT_APP_API_URL}/personnel/frequentiels?year=${today.getUTCFullYear()}`, '_blank').focus(),
            [t('Aperçu prestations à la demande')]: () => window.open(`${process.env.REACT_APP_API_URL}/personnel/a-la-demande?year=${today.getUTCFullYear()}`, '_blank').focus(),
            [t('Controler géodynamics')]: () => navigate(`/personnel/controle`)
        }} actions={actions} filter={{
            [t('Actifs')]: `or2:dateDebut:lte=${encodeURIComponent(today.toISOString())}&or2:dateDebut=&or3:dateFin:gt=${encodeURIComponent(today.toISOString())}&or3:dateFin=`,
            [t('Passés')]: `dateFin:lte=${encodeURIComponent(today.toISOString())}`,
            [t('Futurs')]: `dateDebut:gte=${encodeURIComponent(today.toISOString())}`,
            [t('Tous')]: ``,
        }} {...props} />
        <CopyTempsPopup copiedPersonnel={copiedPersonnel} setCopiedPersonnel={setCopiedPersonnel} reload={reload} />
    </>
}

export function PersonnelDetail(props) {
    const [copiedPersonnel, setCopiedPersonnel] = useState()
    const [reload, setReload] = useState()
    const t = useTranslations()
    const actions = {
        ...useActions(),
        [t("Copier prestations d'un autre préposé")]: (item, reload) => {
            setCopiedPersonnel(item)
            setReload(() => reload)
        }
    }
    return <>
        <DetailPage model="personnel" title={t("Personnel")} Fields={PersonnelFields} actions={actions} defaults={{
            fonction: 'PREPOSE'
        }} tables={{
            [t("Temps")]: <TempsList relation="personnelId" personnel root="." />,
            [t("Historique")]: <HistoriquesList relation="personnelId" root="." />,
        }} {...props} />
        <CopyTempsPopup copiedPersonnel={copiedPersonnel} setCopiedPersonnel={setCopiedPersonnel} reload={reload} />
    </>
}

function CopyTempsPopup({ copiedPersonnel, setCopiedPersonnel, reload }) {
    const [payload, setPayload, set] = useObjectState()
    const personnelOptions = useOptions('personnel', ['prenom', 'nom'], { query: 'fonction=PREPOSE', label: x => `${x.prenom || ''} ${x.nom || ''}` })
    const t = useTranslations()
    const api = useApi()

    const submit = async () => {
        api.post(`/personnel/${copiedPersonnel.id}/copy-temps`, payload).then(reload)
        cancel()
    }
    const cancel = () => {
        setCopiedPersonnel(null)
        setPayload({})
    }

    return <Popup value={!!copiedPersonnel} onChange={cancel}>
        <Form onSubmit={submit}>
            <AsyncSelect label={t("Personnel à copier")} value={payload.personnelId} onChange={set('personnelId')} options={personnelOptions} required />
            <Row className="mt-3">
                <DateInput label={t("Date de début")} value={payload.dateDebut} onChange={set('dateDebut')} required />
                <DateInput label={t("Date de fin")} value={payload.dateFin} onChange={set('dateFin')} />
            </Row>
            <Row className="mt-7">
                <Button gray icon={<FaBan />} onClick={cancel}>{t('Cancel')}</Button>
                <Button icon={<FaSave />} submit>{t('Save')}</Button>
            </Row>
        </Form>
    </Popup>
}

export function PersonnelControle() {
    const [file, setFile] = useState()
    const [loaded, setLoaded] = useState()
    const [declarationsDeTravaux, setDeclarationsDeTravaux] = useState()

    const t = useTranslations()
    const api = useApi()

    const save = async () => {
        const result = await api.post(`/personnel/check-geodynamics`, { file })
        setDeclarationsDeTravaux(result)
        setLoaded(true)
    }

    return <div className="grow w-full">
        <h1 className="text-2xl font-bold mb-3">{t('Contrôle géodynamics')}</h1>
        {!loaded
            ? <Form onSubmit={save} className="flex">
                <div className="bg-white p-5 border border-gray-300 rounded-xl flex flex-col gap-5">
                    <FileInput label={t('Fichier')} value={file} onChange={setFile} required />
                    <Row>
                        <Button icon={<FaSave />} onClick={save}>{t('Vérifier')}</Button>
                    </Row>
                </div>
            </Form>
            : <div className="flex flex-col gap-3">
                {Object.entries(declarationsDeTravaux).map(([numDeclarationTravaux, prestations]) => <div>
                    <h2 className="text-lg mb-2 underline">{numDeclarationTravaux}</h2>
                    <div className="overflow-auto border border-gray-300 bg-white rounded-xl">
                        <div className="grid grid-cols-[repeat(9,auto)]">
                            <div className="px-3 py-2 first:pl-5 last:pr-5 text-left bg-gray-200 font-bold">{t('Date')}</div>
                            <div className="px-3 py-2 first:pl-5 last:pr-5 text-left bg-gray-200 font-bold">{t('Nom des chantiers')}</div>
                            <div className="px-3 py-2 first:pl-5 last:pr-5 text-left bg-gray-200 font-bold">{t('Heures prévues')}</div>
                            <div className="px-3 py-2 first:pl-5 last:pr-5 text-left bg-gray-200 font-bold">{t('Préposé prévu')}</div>
                            <div className="px-3 py-2 first:pl-5 last:pr-5 text-left bg-gray-200 font-bold">{t('Nom des check-in')}</div>
                            <div className="px-3 py-2 first:pl-5 last:pr-5 text-left bg-gray-200 font-bold">{t('Préposé')}</div>
                            <div className="px-3 py-2 first:pl-5 last:pr-5 text-left bg-gray-200 font-bold">{t('Entreprise')}</div>
                            <div className="px-3 py-2 first:pl-5 last:pr-5 text-left bg-gray-200 font-bold">{t('Début')}</div>
                            <div className="px-3 py-2 first:pl-5 last:pr-5 text-left bg-gray-200 font-bold">{t('Fin')}</div>
                            {prestations.map(prestation => <>
                                <div className={`px-3 py-2 first:pl-5 last:pr-5 border-t border-gray-300 whitespace-nowrap md:whitespace-normal ${!prestation.clockings.length ? 'bg-red-300 border-red-300' : !prestation.chantiers.length ? 'bg-yellow-300 border-yellow-300' : ''}`}>{prestation.date.toLocaleDateString()}</div>
                                <div className={`px-3 py-2 first:pl-5 last:pr-5 border-t border-gray-300 whitespace-nowrap md:whitespace-normal ${!prestation.clockings.length ? 'bg-red-300 border-red-300' : !prestation.chantiers.length ? 'bg-yellow-300 border-yellow-300' : ''}`}><div className="flex flex-col gap-2">{prestation.chantiers.map(chantier => <div>{chantier.temps.map(temp => <FkLink to={`/chantiers/${chantier.id}`}>{chantier.nom}</FkLink>)}</div>)}</div></div>
                                <div className={`px-3 py-2 first:pl-5 last:pr-5 border-t border-gray-300 whitespace-nowrap md:whitespace-normal ${!prestation.clockings.length ? 'bg-red-300 border-red-300' : !prestation.chantiers.length ? 'bg-yellow-300 border-yellow-300' : ''}`}><div className="flex flex-col gap-2">{prestation.chantiers.map(chantier => <div>{chantier.temps.map(temp => <FkLink to={`/personnel/${temp.personnelId}`}>{temp.personnelPrenom} {temp.personnelNom}</FkLink>)}</div>)}</div></div>
                                <div className={`px-3 py-2 first:pl-5 last:pr-5 border-t border-gray-300 whitespace-nowrap md:whitespace-normal ${!prestation.clockings.length ? 'bg-red-300 border-red-300' : !prestation.chantiers.length ? 'bg-yellow-300 border-yellow-300' : ''}`}><div className="flex flex-col gap-2">{prestation.chantiers.map(chantier => <div>{chantier.temps.map(temp => <div>{temp.heures}h</div>)}</div>)}</div></div>
                                <div className={`px-3 py-2 first:pl-5 last:pr-5 border-t border-gray-300 whitespace-nowrap md:whitespace-normal ${!prestation.clockings.length ? 'bg-red-300 border-red-300' : !prestation.chantiers.length ? 'bg-yellow-300 border-yellow-300' : ''}`}>{prestation.clockings.map(clocking => <div>{clocking.chantierNom}</div>)}</div>
                                <div className={`px-3 py-2 first:pl-5 last:pr-5 border-t border-gray-300 whitespace-nowrap md:whitespace-normal ${!prestation.clockings.length ? 'bg-red-300 border-red-300' : !prestation.chantiers.length ? 'bg-yellow-300 border-yellow-300' : ''}`}>{prestation.clockings.map(clocking => <div>{clocking.personnelNom}</div>)}</div>
                                <div className={`px-3 py-2 first:pl-5 last:pr-5 border-t border-gray-300 whitespace-nowrap md:whitespace-normal ${!prestation.clockings.length ? 'bg-red-300 border-red-300' : !prestation.chantiers.length ? 'bg-yellow-300 border-yellow-300' : ''}`}>{prestation.clockings.map(clocking => <div>{clocking.fournisseurNom}</div>)}</div>
                                <div className={`px-3 py-2 first:pl-5 last:pr-5 border-t border-gray-300 whitespace-nowrap md:whitespace-normal ${!prestation.clockings.length ? 'bg-red-300 border-red-300' : !prestation.chantiers.length ? 'bg-yellow-300 border-yellow-300' : ''}`}>{prestation.clockings.map(clocking => <div>{clocking.debut}</div>)}</div>
                                <div className={`px-3 py-2 first:pl-5 last:pr-5 border-t border-gray-300 whitespace-nowrap md:whitespace-normal ${!prestation.clockings.length ? 'bg-red-300 border-red-300' : !prestation.chantiers.length ? 'bg-yellow-300 border-yellow-300' : ''}`}>{prestation.clockings.map(clocking => <div>{clocking.fin}</div>)}</div>
                            </>)}
                        </div>
                    </div>
                </div>)}
            </div>}
    </div>
}